import { useState, useEffect, useRef, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useOrgPlanLimits } from '@/hooks/useOrgPlanLimits';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Clock, Coins, Sparkles, ImageIcon, Link2, Loader2, PanelBottom, Save, Upload, X, Plus, Building2, Share2, Instagram, Facebook, Linkedin, MessageCircle, MapPin, Phone, Globe, Copy, ExternalLink } from 'lucide-react';
import {
  AdminPage,
  EmptyState,
  IconTile,
  PageHeader,
  Panel,
  StatusChip,
  adminButton,
  useAdminPageTitle,
  type IconTileProps,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import { LogoCropper } from '@/components/admin/LogoCropper';
import { AiLogoMakerDialog } from '@/components/admin/AiLogoMakerDialog';
import { CURRENCIES, getCurrencySetting } from '@/lib/currency';
import { COMMON_TIMEZONES } from '@/lib/timezones';
import {
  AdminSocialLinks,
  CompanyInfoAdmin,
  useCompanyInfoAdminData,
  useSaveCompanyInfo,
} from '@/hooks/useCompanyInfo';
import {
  RESERVED_SLUGS,
  TENANT_DOMAIN,
  buildWebsiteUrl,
  slugify,
  useOrgSlugInfo,
  useUpdateOrgSlug,
} from '@/hooks/useOrgSlug';

type SocialLinks = AdminSocialLinks;

type AreaRadii = Record<string, number>; // areaName -> km

const getErrorDescription = (error: unknown) => {
  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') return error.message;
    if ('code' in error && typeof error.code === 'string') return error.code;
  }
  return JSON.stringify(error);
};

const RADIUS_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Just this area' },
  { value: 5, label: '+5 km' },
  { value: 10, label: '+10 km' },
  { value: 20, label: '+20 km' },
  { value: 50, label: '+50 km' },
];

const LOGO_PREVIEW_MAX_DIMENSION = 1600;

const createLogoPreviewUrl = async (file: File): Promise<string> => {
  const bitmap = await createImageBitmap(file);
  const longestSide = Math.max(bitmap.width, bitmap.height);

  if (longestSide <= LOGO_PREVIEW_MAX_DIMENSION) {
    bitmap.close();
    return URL.createObjectURL(file);
  }

  const scale = LOGO_PREVIEW_MAX_DIMENSION / longestSide;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return URL.createObjectURL(file);
  }

  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png', 0.92);
  });

  return URL.createObjectURL(blob ?? file);
};

const CompanyInfoAdmin = () => {
  useAdminPageTitle('Company Info');
  const { org, orgId, loading: orgLoading, canWrite } = useAdminOrg();
  const { isOrgOwner, isPlatformAdmin, refreshOrgMembership } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoAdmin | null>(null);
  const [newArea, setNewArea] = useState('');
  const [cropperOpen, setCropperOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string | null>(null);
  const [subdomain, setSubdomain] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [aiLogoOpen, setAiLogoOpen] = useState(false);
  const { data: planLimits, isLoading: planLoading } = useOrgPlanLimits(orgId);
  // Unreadable limits (null) leave it to the function, which re-checks the plan.
  const aiLogoAllowed = planLoading ? undefined : planLimits ? planLimits.aiLogoMaker : true;

  const { data, isLoading, isError, error } = useCompanyInfoAdminData(orgId, orgLoading);
  const saveMutation = useSaveCompanyInfo();
  const { data: slugInfo } = useOrgSlugInfo(orgId);
  const updateSlugMutation = useUpdateOrgSlug();
  const canHidePoweredBy = Boolean(planLimits?.hidePoweredBy);

  // Seed local form state when query data arrives
  useEffect(() => {
    if (data !== undefined) {
      setCompanyInfo(data);
    }
  }, [data]);

  // The saved website address (slug) is the source of truth for the public URL.
  // Prefer the freshly-read value; fall back to OrgContext's resolved slug.
  const savedSlug = slugInfo?.slug ?? org?.slug ?? '';
  const slugLocked = slugInfo?.locked ?? false;

  // Seed the editable subdomain from the saved slug, and re-sync after a change
  // lands (the query invalidates and savedSlug updates).
  useEffect(() => {
    if (savedSlug) setSubdomain(savedSlug);
  }, [savedSlug]);

  // Surface query errors as toasts
  useEffect(() => {
    if (isError && error) {
      toast({ title: 'Error', description: getErrorDescription(error), variant: 'destructive' });
    }
  }, [isError, error, toast]);

  const loading = isLoading || (orgLoading && !orgId);

  // Public website link owners can share on social media, derived from the
  // saved org slug (the editable field below is a pending change until saved).
  const websiteUrl = savedSlug ? buildWebsiteUrl(savedSlug) : null;

  // Pending website-address edit + inline validation.
  const slugChanged = Boolean(savedSlug) && subdomain !== savedSlug;
  const subdomainError = (() => {
    if (!slugChanged) return null;
    if (subdomain.length < 3) return 'Website address must be at least 3 characters.';
    if (RESERVED_SLUGS.has(subdomain)) return 'That website address is reserved — please choose another.';
    return null;
  })();
  const nextChangeDate = slugInfo?.nextChangeAllowedAt
    ? new Date(slugInfo.nextChangeAllowedAt).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  // Keep the website address in lockstep with the company name while the two
  // are still linked (slug derived from the current name). Once an owner has
  // customised the slug — or the cooldown has locked it — renaming leaves the
  // address untouched so existing links are never silently changed.
  const handleCompanyNameChange = (value: string) => {
    if (!companyInfo) return;
    const prevName = companyInfo.company_name;
    setCompanyInfo({ ...companyInfo, company_name: value });
    if (!slugLocked && subdomain === slugify(prevName)) {
      setSubdomain(slugify(value));
    }
  };

  const handleSubdomainChange = (value: string) => {
    setSubdomain(slugify(value));
  };

  const copyWebsiteUrlToClipboard = async (description: string) => {
    if (!websiteUrl) return false;
    try {
      await navigator.clipboard.writeText(websiteUrl);
      toast({ title: 'Copied', description });
      return true;
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy the link', variant: 'destructive' });
      return false;
    }
  };

  const copyWebsiteUrl = async () => {
    await copyWebsiteUrlToClipboard('Website link copied to clipboard');
  };

  const shareWebsiteUrl = async () => {
    if (!websiteUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: companyInfo?.company_name || 'My website', url: websiteUrl });
        return;
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
    }
    await copyWebsiteUrlToClipboard('Sharing is not available, so the link was copied instead');
  };

  const handleSave = async () => {
    if (!companyInfo) return;
    if (!canWrite || (!isOrgOwner && !isPlatformAdmin)) {
      toast({ title: 'Read only', description: canWrite ? 'Only the business owner can change company info' : 'Read-only while impersonating.', variant: 'destructive' });
      return;
    }
    // Block a website-address change up front when it can't succeed, with copy
    // that explains why — rather than letting the RPC reject opaquely.
    if (slugChanged) {
      if (slugLocked) {
        toast({
          title: 'Website address locked',
          description: `You can only change your website address once every 30 days.${nextChangeDate ? ` You can change it again on ${nextChangeDate}.` : ''}`,
          variant: 'destructive',
        });
        return;
      }
      if (subdomainError) {
        toast({ title: 'Invalid website address', description: subdomainError, variant: 'destructive' });
        return;
      }
    }

    setSaving(true);

    try {
      await saveMutation.mutateAsync({ orgId: orgId!, companyInfo });
      // Changing the slug rewrites the public URL, so only do it when the owner
      // actually edited the address. The RPC enforces the cooldown/uniqueness
      // server-side and raises a descriptive error we surface verbatim.
      if (slugChanged) {
        await updateSlugMutation.mutateAsync({ orgId: orgId!, slug: subdomain });
      }
      await refreshOrgMembership();
      toast({
        title: 'Success',
        description: slugChanged ? 'Settings saved and website address updated' : 'Settings updated',
      });
    } catch (err: unknown) {
      toast({ title: 'Error', description: getErrorDescription(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!canWrite || (!isOrgOwner && !isPlatformAdmin)) {
      toast({
        title: 'Logo upload unavailable',
        description: 'Your account does not have permission to upload this company logo.',
        variant: 'destructive',
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      if (tempImageSrc) URL.revokeObjectURL(tempImageSrc);
      const imageUrl = await createLogoPreviewUrl(file);
      setTempImageSrc(imageUrl);
      setCropperOpen(true);
    } catch (err: unknown) {
      toast({ title: 'Error', description: getErrorDescription(err), variant: 'destructive' });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCroppedLogo = async (croppedBlob: Blob) => {
    if (!companyInfo) return;
    if (!canWrite || (!isOrgOwner && !isPlatformAdmin)) {
      toast({
        title: 'Logo upload unavailable',
        description: 'Your account does not have permission to upload this company logo.',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      const filePath = `${orgId}/company/logo.png`;

      const { error: uploadError } = await supabase.storage
        .from('content-images')
        .upload(filePath, croppedBlob, { upsert: true, contentType: 'image/png' });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('content-images')
        .getPublicUrl(filePath);

      const logoUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const updatedCompanyInfo = { ...companyInfo, logo_url: logoUrl };
      await saveMutation.mutateAsync({ orgId: orgId!, companyInfo: updatedCompanyInfo });
      setCompanyInfo(updatedCompanyInfo);

      toast({ title: 'Success', description: 'Logo uploaded and saved' });
    } catch (err: unknown) {
      toast({ title: 'Error', description: getErrorDescription(err), variant: 'destructive' });
    } finally {
      setUploading(false);
      if (tempImageSrc) {
        URL.revokeObjectURL(tempImageSrc);
        setTempImageSrc(null);
      }
    }
  };

  /** Save the logo picked in the AI logo maker (already uploaded) as the company logo. */
  const saveAiLogo = async (logoUrl: string) => {
    if (!companyInfo || !orgId) return;
    if (!canWrite || (!isOrgOwner && !isPlatformAdmin)) {
      throw new Error('Only the business owner can change the logo.');
    }
    const updatedCompanyInfo = { ...companyInfo, logo_url: logoUrl };
    await saveMutation.mutateAsync({ orgId, companyInfo: updatedCompanyInfo });
    setCompanyInfo(updatedCompanyInfo);
    toast({ title: 'Logo saved', description: 'Your new logo is now on your site.' });
  };

  const handleAreaInputChange = (value: string) => {
    setNewArea(value);
  };

  const addArea = (rawValue?: string) => {
    const value = (rawValue ?? newArea).trim();
    if (!value || !companyInfo) return;
    if (companyInfo.service_areas.includes(value)) {
      toast({ title: 'Area already exists', variant: 'destructive' });
      return;
    }
    setCompanyInfo({
      ...companyInfo,
      service_areas: [...companyInfo.service_areas, value],
    });
    setNewArea('');
  };

  const removeArea = (index: number) => {
    if (!companyInfo) return;
    const removed = companyInfo.service_areas[index];
    const nextRadii = { ...companyInfo.service_area_radii };
    delete nextRadii[removed];
    setCompanyInfo({
      ...companyInfo,
      service_areas: companyInfo.service_areas.filter((_, i) => i !== index),
      service_area_radii: nextRadii,
    });
  };

  const setAreaRadius = (areaName: string, km: number) => {
    if (!companyInfo) return;
    const next = { ...companyInfo.service_area_radii } as AreaRadii;
    if (km === 0) {
      delete next[areaName];
    } else {
      next[areaName] = km;
    }
    setCompanyInfo({ ...companyInfo, service_area_radii: next });
  };

  const updateSocialLink = (platform: keyof SocialLinks, field: 'enabled' | 'url', value: boolean | string) => {
    if (!companyInfo) return;
    setCompanyInfo({
      ...companyInfo,
      social_links: {
        ...companyInfo.social_links,
        [platform]: {
          ...companyInfo.social_links[platform],
          [field]: value,
        },
      },
    });
  };

  const updateCurrency = (code: string) => {
    if (!companyInfo) return;
    const currency = getCurrencySetting(code);
    setCompanyInfo({
      ...companyInfo,
      currency_code: currency.code,
      currency_name: currency.name,
      currency_symbol: currency.symbol,
    });
  };

  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case 'instagram': return <Instagram />;
      case 'facebook': return <Facebook />;
      case 'tiktok': return (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      );
      case 'linkedin': return <Linkedin />;
      case 'whatsapp': return <MessageCircle />;
      default: return null;
    }
  };

  const getPlatformLabel = (platform: string) => {
    const labels: Record<string, string> = {
      instagram: 'Instagram',
      facebook: 'Facebook',
      tiktok: 'TikTok',
      linkedin: 'LinkedIn',
      whatsapp: 'WhatsApp',
    };
    return labels[platform] || platform;
  };

  const getPlaceholder = (platform: string) => {
    const placeholders: Record<string, string> = {
      instagram: 'https://instagram.com/yourpage',
      facebook: 'https://facebook.com/yourpage',
      tiktok: 'https://tiktok.com/@yourpage',
      linkedin: 'https://linkedin.com/company/yourcompany',
      whatsapp: 'https://wa.me/447123456789',
    };
    return placeholders[platform] || '';
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!companyInfo) {
    return (
      <AdminPage width="narrow">
        <Panel>
          <EmptyState
            icon={<Building2 />}
            title={orgId ? 'No company info found.' : 'No organization selected.'}
            description="Company details appear here once your workspace is set up."
          />
        </Panel>
      </AdminPage>
    );
  }

  const enabledSocialCount = (Object.keys(companyInfo.social_links) as Array<keyof SocialLinks>).filter(
    (platform) => companyInfo.social_links[platform].enabled,
  ).length;

  return (
    <AdminPage width="narrow" className="adm-form">
      <PageHeader description="Manage your company details displayed across the website" />

      <div className="grid gap-4 md:gap-5 lg:grid-cols-2">
        {/* Public Website Link */}
        {websiteUrl && (
          <SettingsPanel
            className="lg:col-span-2"
            icon={<Globe />}
            tone="violet"
            title="Your Website Link"
            description="Share this link on social media so customers can find and book you. Example format: https://YOUR-SLUG.thepolishedversion.com"
          >
            <div className="flex flex-col gap-2.5 md:flex-row md:items-center">
              <div className="relative min-w-0 md:flex-1">
                <span
                  className="pointer-events-none absolute left-3 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-[hsl(146_55%_45%)] shadow-[0_0_0_3px_hsl(146_55%_45%/0.15)]"
                  aria-hidden="true"
                />
                <Input
                  readOnly
                  value={websiteUrl}
                  aria-label="Public website link"
                  className="!bg-muted/40 pl-8 font-medium"
                  onFocus={(e) => e.currentTarget.select()}
                />
              </div>
              <div className="grid grid-cols-2 gap-2 md:flex md:shrink-0">
                <Button onClick={copyWebsiteUrl} variant="outline" size="sm" className={adminButton.outline}>
                  <Copy className="h-4 w-4" />
                  Copy link
                </Button>
                <Button onClick={shareWebsiteUrl} variant="outline" size="sm" className={adminButton.outline}>
                  <Share2 className="h-4 w-4" />
                  Share link
                </Button>
                <Button asChild size="sm" className={cn(adminButton.primary, 'col-span-2')}>
                  <a href={websiteUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                    View website
                  </a>
                </Button>
              </div>
            </div>
          </SettingsPanel>
        )}

        {/* Website Address (subdomain) */}
        {savedSlug && (
          <SettingsPanel
            className="lg:col-span-2"
            icon={<Link2 />}
            tone="blue"
            title="Website Address"
            description="This is the subdomain customers use to reach you. By default it follows your company name. You can change it at most once every 30 days — changing it replaces your current link, so any address you've already shared will stop working."
            actions={slugLocked ? <StatusChip tone="amber">Locked</StatusChip> : null}
          >
            <div className="space-y-2">
              <Label htmlFor="website-subdomain">Subdomain</Label>
              <div
                className={cn(
                  'flex h-9 items-center overflow-hidden rounded-[9px] border border-input bg-card shadow-[var(--adm-shadow-xs)] transition-shadow focus-within:border-primary/40 focus-within:ring-[3px] focus-within:ring-primary/10',
                  (!canWrite || slugLocked) && 'bg-muted/60',
                  subdomainError && 'border-destructive/50',
                )}
              >
                <Input
                  id="website-subdomain"
                  aria-label="Website address"
                  value={subdomain}
                  onChange={(e) => handleSubdomainChange(e.target.value)}
                  disabled={!canWrite || slugLocked}
                  placeholder="your-business"
                  className="min-w-0 flex-1 rounded-none !h-full !border-0 !bg-transparent !shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <span className="flex h-full select-none items-center whitespace-nowrap border-l border-border bg-muted/50 px-3 text-[13px] text-muted-foreground">
                  .{TENANT_DOMAIN}
                </span>
              </div>
              {subdomainError && <p className="text-xs text-destructive">{subdomainError}</p>}
              {slugLocked ? (
                <p className="text-xs text-amber-700 dark:text-amber-500">
                  Your website address was changed recently and is locked for now.
                  {nextChangeDate ? ` You can change it again on ${nextChangeDate}.` : ''}
                </p>
              ) : slugChanged && !subdomainError ? (
                <p className="adm-hint">
                  Saving changes your link to {buildWebsiteUrl(subdomain)}. Your old address will no longer work.
                </p>
              ) : (
                <p className="adm-hint">
                  Letters, numbers and hyphens only. Changing this counts towards your once-every-30-days limit.
                </p>
              )}
            </div>
          </SettingsPanel>
        )}

        {/* Company Name & Logo */}
        <SettingsPanel icon={<Building2 />} tone="mint" title="Brand Identity" description="Your company name and logo">
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="company-name">Company Name</Label>
              <Input
                id="company-name"
                value={companyInfo.company_name}
                disabled={!canWrite}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                placeholder="Enter company name"
              />
              <p className="adm-hint">
                Your public display name. While your website address matches your name, renaming updates both together.
              </p>
            </div>

            <div className="space-y-2">
              <p className="adm-field-label">Company Logo</p>
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    'relative flex h-24 min-w-32 max-w-full shrink items-center justify-center rounded-xl border px-4',
                    companyInfo.logo_url ? 'border-border bg-card' : 'border-dashed border-border bg-muted/40',
                  )}
                >
                  {companyInfo.logo_url ? (
                    <img src={companyInfo.logo_url} alt="Company logo" className="h-16 w-auto max-w-[260px] object-contain" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-muted-foreground">
                      <ImageIcon className="h-5 w-5" />
                      <span className="text-[11.5px]">No logo uploaded</span>
                    </div>
                  )}
                  {companyInfo.logo_url && (
                    <button
                      type="button"
                      aria-label="Remove logo"
                      className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-[var(--adm-shadow-sm)] transition-colors hover:border-destructive/30 hover:text-destructive disabled:pointer-events-none disabled:opacity-50"
                      onClick={() => setCompanyInfo({ ...companyInfo, logo_url: null })}
                      disabled={!canWrite}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                  <Label
                    htmlFor="logo-upload"
                    className={cn(
                      'inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-[13px] font-medium text-foreground shadow-[var(--adm-shadow-xs)] transition-colors hover:bg-muted',
                      (!canWrite || uploading) && 'pointer-events-none opacity-50',
                    )}
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? 'Uploading...' : 'Upload Logo'}
                    <input
                      ref={fileInputRef}
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoSelect}
                      disabled={!canWrite || uploading}
                    />
                  </Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className={adminButton.soft}
                    disabled={!canWrite || uploading || (!isOrgOwner && !isPlatformAdmin)}
                    onClick={() => setAiLogoOpen(true)}
                  >
                    <Sparkles />
                    Create a logo with AI
                    {aiLogoAllowed === false && (
                      <span className="rounded-full bg-card px-1.5 py-px text-[10.5px] font-semibold text-muted-foreground">Team</span>
                    )}
                  </Button>
                  </div>
                  <p className="adm-hint">Displays at 72-84px height. Upload to crop and replace, or design one with AI.</p>
                </div>
              </div>
            </div>
          </div>
        </SettingsPanel>

        <AiLogoMakerDialog
          open={aiLogoOpen}
          onOpenChange={setAiLogoOpen}
          orgId={orgId ?? null}
          allowed={aiLogoAllowed}
          defaultName={companyInfo.company_name || org?.name || ''}
          onSave={saveAiLogo}
        />

        {/* Logo Cropper Dialog */}
        {tempImageSrc && (
          <LogoCropper
            open={cropperOpen}
            onOpenChange={(open) => {
              setCropperOpen(open);
              if (!open && tempImageSrc) {
                URL.revokeObjectURL(tempImageSrc);
                setTempImageSrc(null);
              }
            }}
            imageSrc={tempImageSrc}
            onCropComplete={handleCroppedLogo}
          />
        )}

        {/* Footer Tagline & Phone */}
        <SettingsPanel
          icon={<PanelBottom />}
          tone="amber"
          title="Footer Content"
          description="Tagline and contact details displayed in the footer"
        >
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <Label htmlFor="footer-tagline">Tagline</Label>
                <span className="adm-num text-[11.5px] text-muted-foreground">
                  {(companyInfo.footer_tagline?.length || 0)} characters
                </span>
              </div>
              <Textarea
                id="footer-tagline"
                value={companyInfo.footer_tagline || ''}
                disabled={!canWrite}
                onChange={(e) => setCompanyInfo({ ...companyInfo, footer_tagline: e.target.value })}
                placeholder="Enter footer tagline..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="footer-phone" className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Phone Number
              </Label>
              <Input
                id="footer-phone"
                value={companyInfo.phone_number || ''}
                disabled={!canWrite}
                onChange={(e) => setCompanyInfo({ ...companyInfo, phone_number: e.target.value })}
                placeholder="e.g. 029 2000 0000"
              />
              <p className="adm-hint">Displayed in footer contact section</p>
            </div>
            <div className="flex items-start justify-between gap-4 border-t border-border pt-5">
              <div className="space-y-1">
                <Label htmlFor="footer-powered-by">Show “Powered by The Polished Version”</Label>
                <p className="adm-hint">
                  {canHidePoweredBy ? (
                    'A small credit on its own line at the very bottom of your footer. Switch it off to keep your site fully your own brand.'
                  ) : (
                    <>
                      Always shown on Solo and Team. Business plans can switch it off.{' '}
                      {isOrgOwner && (
                        <Link to="/admin/billing" className="font-medium text-primary underline underline-offset-2">
                          See plans
                        </Link>
                      )}
                    </>
                  )}
                </p>
              </div>
              <Switch
                id="footer-powered-by"
                className="mt-0.5 shrink-0"
                checked={!canHidePoweredBy || !companyInfo.hide_powered_by}
                disabled={!canWrite || !canHidePoweredBy}
                onCheckedChange={(checked) => setCompanyInfo({ ...companyInfo, hide_powered_by: !checked })}
              />
            </div>
          </div>
        </SettingsPanel>

        {/* Currency */}
        <SettingsPanel
          icon={<Coins />}
          tone="green"
          title="Default Currency"
          description="Used across prices, booking totals, and dashboard revenue."
        >
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select disabled={!canWrite} value={companyInfo.currency_code} onValueChange={updateCurrency}>
              <SelectTrigger id="currency">
                <SelectValue placeholder="Choose currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((currency) => (
                  <SelectItem key={currency.code} value={currency.code}>
                    {currency.code} · {currency.name} ({currency.symbol})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <span className="adm-chip adm-chip--grey">Code · {companyInfo.currency_code}</span>
              <span className="adm-chip adm-chip--grey">Symbol · {companyInfo.currency_symbol}</span>
            </div>
          </div>
        </SettingsPanel>

        {/* Timezone */}
        <SettingsPanel
          icon={<Clock />}
          tone="blue"
          title="Booking Timezone"
          description="Your local timezone. Booking dates and time slots are offered in this zone, so customers in other timezones can't book a slot that's already passed for you."
        >
          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Select
              value={companyInfo.timezone}
              disabled={!canWrite}
              onValueChange={(value) => setCompanyInfo({ ...companyInfo, timezone: value })}
            >
              <SelectTrigger id="timezone">
                <SelectValue placeholder="Choose timezone" />
              </SelectTrigger>
              <SelectContent>
                {COMMON_TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </SettingsPanel>

        {/* Service Areas */}
        <SettingsPanel
          className="lg:col-span-2"
          icon={<MapPin />}
          tone="rose"
          title="Service Areas"
          description="Areas you cover. Click any tag to add a travel radius beyond it."
          actions={
            <span className="adm-chip adm-chip--grey adm-num">
              {companyInfo.service_areas.length} {companyInfo.service_areas.length === 1 ? 'area' : 'areas'}
            </span>
          }
        >
          <div className="space-y-4">
            {companyInfo.service_areas.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {companyInfo.service_areas.map((area, index) => {
                  const radius = companyInfo.service_area_radii[area] ?? 0;
                  const label = radius > 0 ? `${area} +${radius} km` : area;
                  return (
                    <span
                      key={`${area}-${index}`}
                      className="inline-flex h-8 items-center gap-1 rounded-full border border-primary/15 bg-secondary pl-3 pr-1 text-[12.5px] font-medium text-secondary-foreground"
                    >
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            disabled={!canWrite}
                            className="rounded-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            title="Click to set travel radius"
                          >
                            {label}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-1.5" align="start">
                          <p className="adm-label px-2 pb-1.5 pt-1">Travel radius for {area}</p>
                          <div className="flex flex-col gap-0.5">
                            {RADIUS_OPTIONS.map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                disabled={!canWrite}
                                onClick={() => setAreaRadius(area, opt.value)}
                                className={cn(
                                  'flex items-center justify-between rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-muted',
                                  radius === opt.value && 'bg-secondary font-semibold text-secondary-foreground',
                                )}
                              >
                                {opt.label}
                                {radius === opt.value && <Check className="h-3.5 w-3.5" />}
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <button
                        type="button"
                        disabled={!canWrite}
                        onClick={() => removeArea(index)}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-secondary-foreground/70 transition-colors hover:bg-card hover:text-destructive"
                        aria-label={`Remove ${area}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-3 text-[12.5px] text-muted-foreground">
                No service areas yet — add the towns or postcodes you cover.
              </p>
            )}

            <div className="flex gap-2">
              <div className="min-w-0 flex-1 sm:max-w-md">
                <AddressAutocomplete
                  value={newArea}
                  onChange={handleAreaInputChange}
                  onPlaceSelect={(place) => addArea(place.formattedAddress.split(',')[0].trim())}
                  placeholder="Search for an area..."
                  countries={['gb']}
                  type="regions"
                  className="min-w-0"
                />
              </div>
              <Button disabled={!canWrite} onClick={() => addArea()} variant="outline" size="sm" className={adminButton.outline}>
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
            <p className="adm-hint">
              Pick from Google Places or type and click Add. Click any tag to set a travel radius.
            </p>
          </div>
        </SettingsPanel>

        {/* Social Media Links */}
        <SettingsPanel
          className="lg:col-span-2"
          icon={<Share2 />}
          tone="violet"
          title="Social Media Links"
          description="Enable and configure your social media profiles displayed on the website"
          actions={
            <span className="adm-chip adm-chip--grey adm-num">
              {enabledSocialCount} of {Object.keys(companyInfo.social_links).length} on
            </span>
          }
          bodyClassName="pt-1 md:pt-1"
        >
          <div className="adm-divide">
            {(Object.keys(companyInfo.social_links) as Array<keyof SocialLinks>).map((platform) => {
              const enabled = companyInfo.social_links[platform].enabled;
              return (
                <div key={platform} className="flex flex-col gap-2.5 py-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex items-center gap-3 sm:w-[220px] sm:shrink-0">
                    <IconTile tone={enabled ? 'violet' : 'grey'}>{getSocialIcon(platform)}</IconTile>
                    <span className="text-[13.5px] font-semibold text-foreground">{getPlatformLabel(platform)}</span>
                    <Switch
                      className="ml-auto"
                      aria-label={`Show ${getPlatformLabel(platform)} link`}
                      checked={enabled}
                      disabled={!canWrite}
                      onCheckedChange={(checked) => updateSocialLink(platform, 'enabled', checked)}
                    />
                  </div>
                  <Input
                    value={companyInfo.social_links[platform].url}
                    onChange={(e) => updateSocialLink(platform, 'url', e.target.value)}
                    placeholder={getPlaceholder(platform)}
                    aria-label={`${getPlatformLabel(platform)} URL`}
                    className="flex-1"
                    disabled={!canWrite || !enabled}
                  />
                </div>
              );
            })}
          </div>
        </SettingsPanel>
      </div>

      {/* Sticky save bar */}
      <div className="pointer-events-none sticky bottom-4 z-30 flex justify-end pt-1">
        <div className="adm-panel pointer-events-auto flex w-full items-center gap-3 p-2 shadow-[var(--adm-shadow-md)] sm:w-auto sm:pl-4">
          <p className="hidden min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground sm:block">
            Changes update your public website.
          </p>
          <Button
            onClick={handleSave}
            disabled={saving || !canWrite || (!isOrgOwner && !isPlatformAdmin)}
            size="sm"
            className={cn(adminButton.primary, 'w-full shrink-0 sm:w-auto')}
            title={!canWrite ? 'Read-only while impersonating.' : !isOrgOwner && !isPlatformAdmin ? 'Only the business owner can change company info' : undefined}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </Button>
        </div>
      </div>
    </AdminPage>
  );
};

interface SettingsPanelProps {
  icon: ReactNode;
  tone: IconTileProps['tone'];
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}

/** Settings section: tinted icon tile, h2 title, muted description, then the fields. */
const SettingsPanel = ({ icon, tone, title, description, actions, className, bodyClassName, children }: SettingsPanelProps) => (
  <Panel padded={false} className={className}>
    <header className="flex items-start gap-3 px-4 pt-4 md:px-5 md:pt-5">
      <IconTile tone={tone}>{icon}</IconTile>
      <div className="min-w-0 flex-1 pt-0.5">
        <h2 className="text-[14px] font-semibold leading-tight text-foreground">{title}</h2>
        {description && <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-1.5 pt-0.5">{actions}</div>}
    </header>
    <div className={cn('p-4 pt-4 md:p-5 md:pt-4', bodyClassName)}>{children}</div>
  </Panel>
);

export default CompanyInfoAdmin;
